/**
 * Simplified DeviceUUID utility for browser fingerprinting.
 * Based on biggora/device-uuid library but adapted for TypeScript/modern browsers.
 * 
 * Generates a consistent UUID based on browser/device characteristics.
 */

interface DeviceInfo {
  platform: string;
  userAgent: string;
  language: string;
  colorDepth: number;
  pixelDepth: number;
  screenResolution: string;
  timezone: number;
  cpuCores: number;
}

/**
 * Collects device information for fingerprinting.
 */
function getDeviceInfo(): DeviceInfo {
  return {
    platform: navigator.platform || 'unknown',
    userAgent: navigator.userAgent || 'unknown',
    language: navigator.language || 'unknown',
    colorDepth: screen.colorDepth || -1,
    pixelDepth: screen.pixelDepth || -1,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: new Date().getTimezoneOffset(),
    cpuCores: navigator.hardwareConcurrency || -1,
  };
}

/**
 * Simple MD5 hash implementation.
 * Note: This is a simplified version. For production, consider using a crypto library.
 */
function simpleHash(str: string): string {
  let hash = 0;
  if (str.length === 0) return hash.toString(16);
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Convert to positive hex string
  return Math.abs(hash).toString(16).padStart(8, '0');
}

/**
 * Generates a UUID v4-like string from device fingerprint.
 */
function generateUUID(fingerprint: string): string {
  const hash = simpleHash(fingerprint);
  
  // Create UUID format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  // where x is any hexadecimal digit and y is one of 8, 9, A, or B
  const uuid = [
    hash.slice(0, 8),
    hash.slice(8, 12).padEnd(4, '0'),
    '4' + hash.slice(12, 15).padEnd(3, '0'), // Version 4
    'b' + hash.slice(15, 18).padEnd(3, '0'), // Variant
    hash.slice(18).padEnd(12, '0'),
  ].join('-');
  
  return uuid;
}

/**
 * Gets a consistent device UUID based on browser fingerprint.
 * This UUID will be the same for the same browser/device combination.
 * 
 * @returns {string} A UUID string representing this device
 */
export function getDeviceUUID(): string {
  const deviceInfo = getDeviceInfo();
  
  // Create fingerprint string from device characteristics
  const fingerprint = [
    deviceInfo.platform,
    deviceInfo.userAgent,
    deviceInfo.language,
    deviceInfo.colorDepth,
    deviceInfo.pixelDepth,
    deviceInfo.screenResolution,
    deviceInfo.timezone,
    deviceInfo.cpuCores,
  ].join(':');
  
  return generateUUID(fingerprint);
}

/**
 * Alternative: Use crypto.randomUUID() for a truly random UUID
 * (not based on fingerprint, so won't be consistent across sessions)
 */
export function getRandomUUID(): string {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}
